import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PawPrint, Sparkle, Heart, HandHeart } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import ShoppableLookbook from "@/components/ShoppableLookbook";
import MissionSection from "@/components/MissionSection";
import OriginStory from "@/components/OriginStory";
import MissionProgress from "@/components/MissionProgress";
import RetroTV from "@/components/RetroTV";
import PackMosaic from "@/components/PackMosaic";
import DriveInTheater from "@/components/DriveInTheater";
import HeavenCredits from "@/components/HeavenCredits";
import FosterNetwork from "@/components/FosterNetwork";
import ArtWorkzBand from "@/components/ArtWorkzBand";
import MissionTicker from "@/components/MissionTicker";
import WarriorsSanctuary from "@/components/WarriorsSanctuary";
import ResurrectionCountdown from "@/components/ResurrectionCountdown";

const CITIES = [
  { key: "vegas", label: "Vegas", tag: "702 · King",
    img: "https://images.unsplash.com/photo-1601056645460-05fd9ad8f4e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" },
  { key: "miami", label: "Miami", tag: "305 · Smiley",
    img: "https://images.unsplash.com/photo-1533106497176-45ae19e68ba2?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" },
  { key: "nyc", label: "NYC / PR", tag: "917 · Beats",
    img: "https://images.unsplash.com/photo-1541702467897-41915a07d3a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" },
];

// 🎬 Paste your Mr. Wigglez brand video URL below to autoplay it as the hero background.
// Leave empty ("") to fall back to the hero image.
const HERO_VIDEO = "https://07970fe6-31d3-43dd-8766-0cbf790c75c8.preview.emergentagent.com/videos/hero.mp4";
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/ea7ac58d7b9647fcb71b9da2405939d59f243fcdf2b65fa20a4f13ac98969065.jpeg";
const SETS_HERO = "https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/1bfbfaf70c5d64584eccfc7f46014b872f6f033fbdaa0b45f4e5b30fb57bf5f0.jpeg";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [sets, setSets] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 4))).catch(() => {});
    api.get("/products").then(({ data }) => setSets(data.filter((p) => (p.tags || []).includes("set-combo")).slice(0, 6))).catch(() => {});
  }, []);

  const shopLook = (p) => { addItem(p, 1, { fit: "full-set" }); toast.success(`${p.name} — full fit added 🐾`); };

  return (
    <div data-testid="home-page">
      {/* Hero — THE SOUL-SHAKER COVER (the true first page) */}
      <section className="relative grain overflow-hidden min-h-[92vh] flex items-center" data-testid="soul-shaker-hero">
        <div className="absolute inset-0">
          {HERO_VIDEO ? (
            <video src={HERO_VIDEO} poster={HERO_IMAGE} autoPlay muted loop playsInline
              className="h-full w-full object-cover object-center" data-testid="hero-video" />
          ) : (
            <img src={HERO_IMAGE} alt="Mr. Wigglez in the Brabus G-Wagon, blue eyes catching the neon light" className="h-full w-full object-cover object-center" data-testid="hero-image" />
          )}
          {/* Slow pulsing neon-pink light bloom */}
          <div className="absolute -left-24 top-1/4 h-[38rem] w-[38rem] rounded-full soul-pulse pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(255,20,147,0.55) 0%, rgba(255,20,147,0) 68%)", filter: "blur(14px)" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/45 to-background/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/70 via-transparent to-background/40" />
        </div>

        <div className="relative w-full max-w-5xl mx-auto px-5 sm:px-8 text-center py-24">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="overline text-primary mb-6 tracking-[0.35em]" data-testid="hero-eyebrow">
            The Mission · Mr. Wigglez
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-6xl lg:text-7xl mx-auto max-w-4xl"
            data-testid="hero-headline">
            What if you were the <span className="neon-text">only one</span> standing in the <span style={{ color: "#ccff00" }}>gap?</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 text-muted-foreground max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            The kids nobody came for. The pits on the shelter clock. Every piece you wear stands in the gap — streetwear meets the mission.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.32 }} className="mt-9">
            <ResurrectionCountdown />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.38 }}
            className="mt-11 flex flex-col sm:flex-row gap-5 justify-center items-center">
            <Link to="/shop" data-testid="hero-armor-btn"
              className="metal-cta metal-cta-pink group inline-flex items-center gap-3 bg-primary text-primary-foreground px-9 py-5 rounded-[12px] font-display font-black uppercase tracking-wide text-base sm:text-lg neon-glow">
              <PawPrint weight="fill" size={22} className="wiggle-icon" />
              Armor Up: Join the Shop
            </Link>
            <Link to="/donate" data-testid="hero-gap-btn"
              className="metal-cta metal-cta-pink group inline-flex items-center gap-3 px-9 py-5 rounded-[12px] font-display font-black uppercase tracking-wide text-base sm:text-lg text-white"
              style={{ background: "linear-gradient(100deg,#ff1493,#c60082)", boxShadow: "0 0 22px rgba(255,20,147,0.5)" }}>
              <Heart weight="fill" size={22} className="wiggle-icon" />
              Stand in the Gap: Donate Now
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-8 flex flex-wrap gap-x-7 gap-y-2 justify-center overline text-muted-foreground">
            <Link to="/try-on" className="hover:text-primary transition-colors inline-flex items-center gap-1.5" data-testid="hero-tryon-btn"><Sparkle weight="fill" size={14} /> Virtual Try-On</Link>
            <Link to="/dog-gear" className="hover:text-secondary transition-colors inline-flex items-center gap-1.5" data-testid="hero-doggear-btn"><PawPrint weight="fill" size={14} /> Shop Dog Gear</Link>
            <Link to="/foster" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="hero-foster-btn"><HandHeart weight="fill" size={14} /> Foster Portal</Link>
          </motion.div>
        </div>
      </section>

      {/* ArtWorkz feature band — collect the art, fund the ranch */}
      <MissionTicker />

      <ArtWorkzBand />

      {/* The Wigglez Drive-In — "HEAVEN" cinematic reel */}
      <DriveInTheater />

      {/* Rolling action credits — thank you, live tracker, tour, share, newsletter */}
      <HeavenCredits />

      {/* Foster Network — practice what we preach */}
      <FosterNetwork />

      {/* P'IT FACTOR shoppable lookbook */}
      <ShoppableLookbook />

      {/* The Pack — tribute */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 border-b border-white/5">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="rounded-2xl overflow-hidden border border-primary/20 neon-glow" data-testid="pack-tribute-img">
            <img src="https://static.prod-images.emergentagent.com/jobs/062e260e-b0f9-432f-a759-5890d554bb91/images/39d7006d34192de75beb02c0f1b48f72318abca8410591c9e1e63fef7d544981.jpeg" alt="Smiley, Beats and King" className="w-full object-cover" />
          </motion.div>
          <div>
            <p className="overline text-primary mb-3">The Pack · In loving memory</p>
            <h2 className="text-4xl sm:text-5xl tracking-tight font-medium mb-5">Smiley, Beats <span className="aqua-text">&amp; King.</span></h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Every fit, every drop and every piece of gear we make is for the pack — <span className="text-white">Smiley</span> (white pit),
              <span className="text-white"> Beats</span> (grey pitbull mastiff) and <span className="text-white">King</span> (blue-nosed grey with the white diamond).
              They're the heart of Mr. Wigglez. Forever in the aura. RIP. 🕊️
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                { n: "Smiley", c: "bg-white" },
                { n: "Beats", c: "bg-muted-foreground" },
                { n: "King", c: "bg-secondary" },
              ].map((d) => (
                <span key={d.n} className="inline-flex items-center gap-2 overline px-4 py-2 rounded-full border border-white/15">
                  <span className={`h-2.5 w-2.5 rounded-full ${d.c}`} /> {d.n}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Choose Your World -> City Collabs */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline text-primary mb-2">Choose your city</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-light">The Collab Drops</h2>
          </div>
          <Link to="/collabs" className="overline text-primary hover:opacity-80 inline-flex items-center gap-1">All collabs <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {CITIES.map((c, i) => (
            <motion.div key={c.key} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}>
              <Link to={`/collabs?city=${c.key}`} className="group block relative h-72 rounded-2xl overflow-hidden border border-white/10 card-hover hover:border-primary/40" data-testid={`city-tile-${c.key}`}>
                <img src={c.img} alt={c.label} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                <div className="relative h-full p-6 flex flex-col justify-end">
                  <Sparkle weight="fill" size={22} className="text-primary mb-3" />
                  <h3 className="text-3xl font-modern font-semibold tracking-tight">{c.label}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{c.tag}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* wiggleS Origin Story */}
      <OriginStory />

      {/* The Pack Mosaic — Smiley made of a thousand rescues, King faded in */}
      <PackMosaic />

      {/* $3M Mission Progress */}
      <MissionProgress />

      {/* Retro TV Broadcast */}
      <RetroTV />

      {/* Mission */}
      <MissionSection />

      <WarriorsSanctuary />

      {/* Featured products */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="overline text-primary mb-2">Handpicked</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium">Featured drops</h2>
            </div>
            <Link to="/shop" className="overline text-primary hover:opacity-80 inline-flex items-center gap-1">View all <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="home-featured">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
